import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
/** Components */
import { Container } from '../../Components/Container';
import { DefaultTable } from 'Components/DefaultTable';
import { PageHelper } from 'Components/PageHelper';
import { Information } from 'Components/Information';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
import { tableHeader } from './table-header.js';
/** Services */
import { getProcessesShortPaged } from 'services/processes-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';

/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { getUsersName } from 'services/users-service';
import { InputLoader } from 'Components/PageLoader/Input';
import { callDeleteAction, callArchiveAction, callUnarchiveAction } from './actions';
import { ModalConfirm } from 'Components/ModalConfirm';

export const Processes = () => {
  const { t } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      description: '',
      user: '',
      dpia: 'all',
      lia: 'all',
      archived: false,
    },
  });

  const TRI_TO_BACKEND = { all: 'Todos', yes: 'Sim', no: 'Não' };

  const getContentSearch = () => {
    const dados = getValues();
    const dpiaParam = TRI_TO_BACKEND[dados.dpia] ?? 'Todos';
    const liaParam = TRI_TO_BACKEND[dados.lia] ?? 'Todos';
    let queryString = `&archived=${dados.archived}&dpia=${dpiaParam}&lia=${liaParam}`;
    if (dados.name !== '') {
      queryString += `&name=${dados.name}`;
    }
    if (dados.description !== '') {
      queryString += `&description=${dados.description}`;
    }
    if (dados.user !== '') {
      queryString += `&user=${dados.user}`;
    }
    if (dados.criticity !== '') {
      queryString += `&criticity=${dados.criticity}`;
    }

    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const {
    isLoading,
    error,
    data: pagedProcesses,
  } = useQuery(['getProcesses', refetch, page], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const queryContent = getContentSearch();
    const result = await getProcessesShortPaged(selectedTenant.slug, page, 15, queryContent);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };

  const onArchive = item => {
    setItemToArchive(item);
    setOpenArchiveModal(true);
  };

  const onCloseArchiveModal = () => {
    setOpenArchiveModal(false);
    setItemToArchive(null);
  };

  const onUnarchive = item => {
    setItemToUnarchive(item);
    setOpenUnarchiveModal(true);
  };

  const onCloseUnarchiveModal = () => {
    setOpenUnarchiveModal(false);
    setItemToUnarchive(null);
  };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch, SwalReact, t);
    } catch (error) {
      onCloseDeleteModal();
      SwalReact.fire({
        title: t('processes.toasts.errorTitle'),
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch, SwalReact, t);
    } catch (error) {
      onCloseArchiveModal();
      SwalReact.fire({
        title: t('processes.toasts.errorTitle'),
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch, SwalReact, t);
    } catch (error) {
      onCloseUnarchiveModal();
      SwalReact.fire({
        title: t('processes.toasts.errorTitle'),
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container title={t('processes.list.title')} breadcrumb={t('processes.list.breadcrumb')} showButtonAdd linkPage="/processes" dataTour="page-title" dataTourAdd="add-button">
      <div data-tour="info-card">
        <Information
          title={t('processes.list.cardTitle')}
          description={<p className="m-0">{t('processes.list.description')}</p>}
          style={{
            marginTop: '24px',
          }}
        />
      </div>
      {!error && (
        <>
          <Card className="mb-2 mt-3" sx={{ padding: '20px' }} data-tour="filter-card">
            <Row className="mb-3" sx={{ alignItems: 'center' }}>
              <Column xs={12} md={4}>
                <div data-tour="filter-name" style={{ width: '100%' }}>
                  <CustomTextField
                    {...register('name')}
                    label={t('processes.list.filters.name')}
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                </div>
              </Column>
              <Column xs={12} md={4}>
                <div data-tour="filter-description" style={{ width: '100%' }}>
                  <CustomTextField
                    {...register('description')}
                    label={t('processes.list.filters.description')}
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                      ' > div': {
                        minHeight: '40px',
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </Column>
              <Column xs={12} md={4}>
                <div data-tour="filter-criticity" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="criticity"
                  render={({ field: { onChange, value } }) => {
                    const getCriticityColor = (level) => {
                      const colors = {
                        '1': '#22c55e', // Verde
                        '2': '#84cc16', // Verde claro
                        '3': '#eab308', // Amarelo
                        '4': '#f97316', // Laranja
                        '5': '#ef4444'  // Vermelho
                      };
                      return colors[level] || '#6b7280';
                    };

                    const getCriticityLabel = (level) => {
                      return t(`processes.criticality.${level}`, level);
                    };
                    
                    return (
                      <FormControl fullWidth>
                        <InputLabel id="criticity-label" shrink>
                          {t('processes.list.filters.criticality')}
                        </InputLabel>
                        <Select
                          labelId="criticity-label"
                          id="criticity"
                          value={value}
                          label={t('processes.list.filters.criticality')}
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                          renderValue={(selected) => {
                            if (!selected) return t('processes.list.filters.selectCriticality');
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: getCriticityColor(selected)
                                  }}
                                />
                                <span>{getCriticityLabel(selected)}</span>
                              </div>
                            );
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            {t('processes.list.filters.selectCriticality')}
                          </MenuItem>
                          {['1','2','3','4','5'].map((item, index) => (
                            <MenuItem value={item} key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: getCriticityColor(item),
                                  border: '1px solid rgba(0,0,0,0.1)',
                                  marginRight: '8px'
                                }}
                              />
                              <span>{getCriticityLabel(item)}</span>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }}
                />
                </div>
              </Column>
            </Row>
            <Row className="mb-3" sx={{ alignItems: 'center' }}>
              <Column xs={12} md={4}>
                <div data-tour="filter-user" style={{ width: '100%' }}>
                  {!userLoading && (
                    <Controller
                    control={control}
                    name="user"
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="user-label" shrink>
                          {t('processes.list.filters.responsible')}
                        </InputLabel>
                        <Select
                          labelId="user-label"
                          id="user"
                          value={value}
                          label={t('processes.list.filters.responsible')}
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            {t('processes.list.filters.selectResponsible')}
                          </MenuItem>
                          {users
                            ?.map(user => user.name)
                            .map((item, index) => (
                              <MenuItem value={item} key={`user-${index}`}>
                                {item}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
                {userLoading && (
                  <FormControl fullWidth sx={{ margin: '0 auto' }}>
                    <InputLabel id="user-label" shrink={true}>
                      {t('processes.list.filters.responsible')}
                    </InputLabel>
                    <InputLoader height={'40px'} />
                  </FormControl>
                )}
                </div>
              </Column>
              <Column xs={12} md={3}>
                <div data-tour="filter-dpia" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="dpia"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="dpia-label" shrink>
                        DPIA
                      </InputLabel>
                      <Select
                        labelId="dpia-label"
                        id="dpia"
                        value={value}
                        label="DPIA"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {['all', 'yes', 'no'].map(item => (
                          <MenuItem value={item} key={item}>
                            {t(`processes.list.filters.options.${item}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={3}>
                <div data-tour="filter-lia" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="lia"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="lia-label" shrink>
                        LIA
                      </InputLabel>
                      <Select
                        labelId="lia-label"
                        id="lia"
                        value={value}
                        label="LIA"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {['all', 'yes', 'no'].map(item => (
                          <MenuItem value={item} key={item}>
                            {t(`processes.list.filters.options.${item}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div data-tour="filter-archived" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <Controller
                    control={control}
                    name="archived"
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      value={value}
                      control={<Checkbox checked={value} onChange={onChange} name="archived" />}
                      label={t('processes.list.filters.archived')}
                      sx={{ marginLeft: 0, whiteSpace: 'nowrap' }}
                    />
                  )}
                />
                </div>
              </Column>
            </Row>
            <Row className="mb-2" sx={{ alignItems: 'center', justifyContent: 'flex-start' }}>
              <Column xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div data-tour="filter-buttons" style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '12px',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  width: '100%'
                }}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      width: '120px',
                      borderRadius: '8px',
                      padding: '8px 20px',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      height: '36px',
                      fontWeight: 500
                    }}
                    onClick={() => {
                      reset();
                      setRefetch(!refetch);
                    }}
                  >
                    {t('processes.list.filters.clear')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      width: '120px',
                      borderRadius: '8px',
                      padding: '8px 20px',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      height: '36px',
                      fontWeight: 500
                    }}
                    endIcon={<SearchOutlinedIcon />}
                    disabled={watch('typeFilter') === ''}
                    onClick={() => {
                      setRefetch(!refetch);
                    }}
                  >
                    {t('processes.list.filters.filter')}
                  </Button>
                </div>
              </Column>
            </Row>
          </Card>
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('processes.list.errorLoad')}</div>}
      {!isLoading && !error && pagedProcesses.data?.length === 0 && (
        <NoData description={t('processes.list.empty')} />
      )}
      {!isLoading && !error && pagedProcesses.data?.length > 0 && (
        <div className="container">
          <div data-tour="table">
            <DefaultTable
            rows={tableHeader(t)}
            cells={pagedProcesses.data}
            onClickDelete={onDelete}
            onClickArchive={onArchive}
            onClickUnarchive={onUnarchive}
            showArchive
            link="processes"
          />
          </div>
          <div data-tour="pagination">
            <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
              <Pagination
              count={pagedProcesses.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
              />
            </Stack>
          </div>
          <ModalConfirmDelete
            description={t('processes.list.modals.deleteMessage', { name: itemToDelete?.name ?? '' })}
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <ModalConfirm
            description={t('processes.list.modals.archiveMessage', { name: itemToArchive?.name ?? '' })}
            open={openArchiveModal}
            onClose={onCloseArchiveModal}
            onConfirm={onConfirmArchiveModal}
          />
          <ModalConfirm
            description={t('processes.list.modals.unarchiveMessage', { name: itemToUnarchive?.name ?? '' })}
            open={openUnarchiveModal}
            onClose={onCloseUnarchiveModal}
            onConfirm={onConfirmUnarchiveModal}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="processes-list" />
    </Container>
  );
};

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo à página de Processos! Aqui você pode visualizar, filtrar e gerenciar todos os processos da organização.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique aqui no título para voltar à lista de processos.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Clique no botão "+" para adicionar um novo processo à sua organização.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="info-card"]',
    content: 'Este card explica a importância de mapear os processos que tratam dados pessoais e impactam os objetivos de negócio.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-card"]',
    content: 'Use este painel de filtros para encontrar processos específicos.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-name"]',
    content: 'Filtre processos pelo nome.',
  },
  {
    target: '[data-tour="filter-description"]',
    content: 'Filtre processos pela descrição.',
  },
  {
    target: '[data-tour="filter-criticity"]',
    content: 'Filtre por criticidade: de 1 (Muito Baixa) a 5 (Muito Alta). As cores ajudam na identificação visual.',
  },
  {
    target: '[data-tour="filter-user"]',
    content: 'Filtre processos por responsável.',
  },
  {
    target: '[data-tour="filter-dpia"]',
    content: 'DPIA (Data Protection Impact Assessment) - Filtre processos que possuem ou não avaliação de impacto à proteção de dados.',
  },
  {
    target: '[data-tour="filter-lia"]',
    content: 'LIA (Legitimate Interest Assessment) - Filtre processos que possuem ou não avaliação de interesse legítimo.',
  },
  {
    target: '[data-tour="filter-archived"]',
    content: 'Marque esta opção para visualizar processos arquivados.',
  },
  {
    target: '[data-tour="filter-buttons"]',
    content: 'Use "Limpar" para resetar todos os filtros ou "Filtrar" para aplicar os critérios selecionados.',
  },
  {
    target: '[data-tour="table"]',
    content: 'Esta tabela exibe todos os processos. Você pode visualizar detalhes, editar, arquivar ou excluir cada processo.',
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas de processos.',
  },
  {
    target: 'body',
    content: 'Tour concluído! Agora você conhece todas as funcionalidades da página de Processos. Use os filtros para encontrar processos específicos e gerencie-os conforme necessário.',
    placement: 'center',
  },
];
